import { useEffect } from "react";

/**
 * Injects a JSON-LD <script> into the document head for the current page, and
 * removes it again when the page unmounts or the data changes.
 *
 * The cleanup matters more than it looks. This is a single-page app: without
 * removing the old block on a route change, a visitor who browsed three
 * listings would end up with three RealEstateListing blocks in the head, two
 * of them describing properties they are no longer looking at. Search engines
 * render the page and would read all three.
 *
 * Pass null while data is still loading; nothing is injected until there is
 * something real to describe.
 */
export default function useJsonLd(data) {
    // Serialising here means the effect re-runs only when the content actually
    // changes, not on every render that rebuilds an equivalent object.
    const json = data ? JSON.stringify(data) : null;

    useEffect(() => {
        if (!json) return undefined;

        /*
         * api/render.js injects a JSON-LD block into the served HTML so that
         * crawlers which do not run JavaScript get structured data. Once this
         * hook has its own, the server's copy has to go, or the page carries
         * two blocks describing the same thing.
         *
         * Only untagged blocks are removed - ours carry data-jsonld - and only
         * once `json` is non-null, so the server's copy survives until there is
         * something to replace it with.
         */
        const serverInjected = document.querySelectorAll(
            'script[type="application/ld+json"]:not([data-jsonld])'
        );
        Array.prototype.forEach.call(serverInjected, function (node) {
            if (node.parentNode) node.parentNode.removeChild(node);
        });

        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.setAttribute("data-jsonld", "page");
        el.text = json;
        document.head.appendChild(el);

        return () => {
            if (el.parentNode) el.parentNode.removeChild(el);
        };
    }, [json]);
}
